import {
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { produce } from 'immer';

import { numbersApi } from 'apis/numbers';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import {
  getNextFunctionalityState,
  smsFunctionalityStateText,
} from '../utils/smsFunctionality';

import styles from './changeSmsFunctionalityModal.module.scss';

/** A component that displays a modal for changing the sms activation status. */
export const ChangeSmsFunctionalityModal = createAppModal(
  { type: 'settings:changeSmsFunctionality' },
  ({ isOpen, modal, meta, hide }): JSX.Element | null => {
    const notify = useNotifications();
    const [updateNumber, { isLoading }] = numbersApi.useUpdateNumberMutation();

    const {
      smsTableDataItem,
      smsTableDataItem: {
        phoneNumber: { sms_functionality_state, e164_format },
      },
    } = modal.arg;

    // field is optional to disconnect ai changes from backend changes
    if (!sms_functionality_state) return null;

    /** Handles sending a request for sms functionality change. */
    const handleConfirm = async () => {
      const nextState = getNextFunctionalityState(sms_functionality_state);

      try {
        const response = await updateNumber({
          phoneNumber: e164_format,
          data: { sms_functionality_state: nextState },
        }).unwrap();

        meta?.resolve({
          updatedSmsTableDataItem: produce(smsTableDataItem, (draft) => {
            draft.phoneNumber.sms_functionality_state =
              response.data.sms_functionality_state;
          }),
        });

        notify(
          {
            title: 'Request for change successfully sent',
            time: 3000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch {
        notify(
          {
            title: 'An error occured while sending the request',
          },
          NotificationType.Error,
        );
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title={
            <FlexBox alignItems="baseline" gap="200">
              <Text.Heading variant="heading-2">
                {smsFunctionalityStateText[sms_functionality_state]}
              </Text.Heading>

              <Text.Paragraph variant="2" subdued>
                {e164_format}
              </Text.Paragraph>
            </FlexBox>
          }
          onClose={hide}
        />

        <ModalBody>
          <Text.Paragraph variant="2" subdued>
            We need to work some magic to change the status of this number. When
            you send this request, we&apos;ll notify our support team
            immediately.
            <br />
            <br />
            This process can take up to 2 business days.
          </Text.Paragraph>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Send request"
          confirmOnClick={handleConfirm}
          confirmProps={{
            isLoading,
          }}
        />
      </Modal>
    );
  },
);
